import { Component, OnInit, inject } from '@angular/core';

import { take } from 'rxjs';

import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';

import {
  AgendaCRUDService,
  AgendaCheckInUserOutput,
  AgendaReadOnlySchema,
  AgendaUserCreateSchema,
  EventUserCreateSchema,
} from '@digitalforest/ngx-oak-api';

import { BaseScanPageComponent } from '../base-scan-page/base-scan-page.component';
import { ERROR_MESSAGES } from '../error-constants';

import { ZXingScannerModule } from '@zxing/ngx-scanner';

@Component({
  selector: 'app-agenda-scan-page',
  templateUrl: './agenda-scan-page.component.html',
  styleUrls: ['./agenda-scan-page.component.scss'],
  standalone: true,
  imports: [BadgeModule, ButtonModule, ProgressBarModule, ZXingScannerModule],
})
export class AgendaScanPageComponent extends BaseScanPageComponent implements OnInit {
  checkInOutput: AgendaCheckInUserOutput | null = null;
  badgeValue = '';
  badgeSeverity: 'success' | 'danger' | 'warning' = 'success';
  checkInPayload = '';
  private userId: number | null = null;
  private readonly eventId: number;
  private readonly agendaId: number;
  agendaData: AgendaReadOnlySchema | null = null;
  private agendaCRUDService = inject(AgendaCRUDService);

  constructor() {
    super();
    this.eventId = parseInt(this.route.snapshot.paramMap.get('event_id') || '0');
    this.agendaId = parseInt(this.route.snapshot.paramMap.get('agenda_id') || '0');
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Load agenda data with counts
    this.agendaCRUDService
      .manageGetAgenda(this.agendaId, this.companyId)
      .pipe(take(1))
      .subscribe({
        next: (value) => {
          this.agendaData = value;
        },
        error: (error) => {
          this.logger.error('[Agenda] Failed to get agenda data', error);
          this.agendaData = null;
        },
      });
  }

  protected handleScan(payload: string): void {
    this.checkInPayload = payload;
    this.userService
      .agendaCheckIn(this.company!.id, this.agendaId, payload)
      .pipe(take(1))
      .subscribe({
        next: (value) => {
          this.logger.debug('[Agenda Check-In] Success response', value);
          this.scannerEnabled = false;
          this.failureCount = 0;

          if (value.status === 'error') {
            this.errorMessage = this.getErrorMessage(value);
            this.checkInOutput = null;
            this.badgeValue = 'Error';
            this.badgeSeverity = 'danger';
          } else {
            this.errorMessage = null;
            this.checkInOutput = value.data!;

            // Check if user is not in agenda (either flag indicates they need enrollment)
            if (value.data?.user_not_in_event || value.data?.user_not_in_agenda) {
              this.badgeValue = 'Not Enrolled';
              this.badgeSeverity = 'warning';
              this.userId = value.data.user.id;
            } else {
              this.badgeValue = 'Success';
              this.badgeSeverity = 'success';
            }

            this.updateGroupInformation(value.data!.group_id_list);
          }

          this.updateCheckInCount();
        },
        error: (error) => {
          this.logger.error('[Agenda Check-In] Failed to process check-in', error);
          this.scannerEnabled = false;
          this.errorMessage = 'Failed to connect to server. Please try again.';
          this.checkInOutput = null;
          this.operationInProgress = false;
        },
        complete: () => {
          this.operationInProgress = false;
        },
      });
  }

  protected setupTestData(): void {
    this.checkInOutput = {
      user: {
        name: 'John',
        surname: 'Doe',
        corporation: 'Test Corporation',
        job_title: 'Software Engineer',
        id: 1,
        company_id: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      event_already_checked_in: false,
      agenda_already_checked_in: false,
      users_in_event_count: 100,
      checked_in_users_in_event_count: 42,
      user_not_in_event: false,
      user_not_in_agenda: false,
      group_id_list: null,
      event_id_list: null,
      agenda_id_list: null,
      users_in_agenda_count: 100,
      checked_in_users_in_agenda_count: 50,
      event_id: 1,
    };

    this.agendaData = {
      id: this.agendaId,
      company_id: this.companyId,
      title: 'Test Agenda',
      users_in_agenda_count: 100,
      checked_in_users_in_agenda_count: 50,
      reserved_seat_users_in_agenda_count: 10,
      general_seat_users_in_agenda_count: 90,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    this.groupInformation = [{ name: 'VIP Guests' }, { name: 'Speakers' }, { name: 'Workshop Attendees' }];

    this.adminCheckInCount = 25;
    this.operationInProgress = false;
  }

  private getErrorMessage(response: { meta?: Record<string, unknown> | null; message?: string | null }): string {
    const errorCode = response.meta?.['error_code'];

    if (errorCode && ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES]) {
      return ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES];
    }

    return response.message || 'An error occurred';
  }

  onAuthorize(): void {
    if (this.userId && this.eventId) {
      this.operationInProgress = true;
      const agendaUserCreateSchema: AgendaUserCreateSchema = {
        user_id: this.userId,
        agenda_id: this.agendaId,
        checked_in: true,
      };

      // Check the flags to determine what we need to do
      if (this.checkInOutput?.user_not_in_event) {
        // User is not in event, need to add to both event and agenda
        const eventUserCreateSchema: EventUserCreateSchema = {
          user_id: this.userId,
          event_id: this.eventId,
          checked_in: true,
        };

        // First add to event
        this.eventUserCRUDService
          .manageCreateEventUser(this.company!.id, eventUserCreateSchema)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.logger.info('[Agenda Authorize] Successfully added user to event');
              // Now add to agenda
              this.addUserToAgenda(agendaUserCreateSchema);
            },
            error: (error) => {
              this.logger.error('[Agenda Authorize] Failed to add user to event', error);
              this.errorMessage = 'Failed to authorize user for event';
              this.badgeValue = 'Error';
              this.badgeSeverity = 'danger';
              this.operationInProgress = false;
            },
          });
      } else {
        // User is already in event, just add to agenda
        this.addUserToAgenda(agendaUserCreateSchema);
      }
    } else {
      this.logger.error('[Agenda Authorize] Missing user ID or event ID for authorization', { userId: this.userId, eventId: this.eventId });
      this.errorMessage = 'Unable to authorize - required information not available';
      this.badgeValue = 'Error';
      this.badgeSeverity = 'danger';
    }
  }

  private addUserToAgenda(agendaUserCreateSchema: AgendaUserCreateSchema): void {
    this.agendaUserCRUDService
      .manageCreateAgendaUser(this.company!.id, agendaUserCreateSchema)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.logger.info('[Agenda Authorize] Successfully authorized user for agenda');
          // Update UI to show success
          if (this.checkInOutput) {
            this.checkInOutput.user_not_in_event = false;
            this.checkInOutput.user_not_in_agenda = false;
          }
          this.errorMessage = null;
          this.badgeValue = 'Success';
          this.badgeSeverity = 'success';
          this.operationInProgress = false;
          this.updateCheckInCount();
        },
        error: (error) => {
          this.logger.error('[Agenda Authorize] Failed to authorize user for agenda', error);
          // Update UI to show error
          this.errorMessage = 'Failed to authorize user for session';
          this.badgeValue = 'Error';
          this.badgeSeverity = 'danger';
          this.operationInProgress = false;
        },
      });
  }
}
